/*
  reveal.js — generic scroll-reveal utility.

  Direct children of a [data-reveal] container start hidden (u-reveal CSS, under
  html[data-js]) and fade + rise into place as they enter the viewport. Items in
  the same intersection batch stagger relative to each other in reading order;
  the stagger counter resets after a scroll-idle gap so an item scrolled to in
  isolation appears immediately instead of inheriting a long cumulative delay.
  Reduced-motion (or no IntersectionObserver) reveals everything at once.

  Markup:  <div data-reveal> ...direct-child items... </div>

  Generalized from plp-card-reveal.js, which keeps its own PLP-specific
  filter-swap handling.
*/
(function () {
  'use strict';

  const STEP_MS = 90; // delay between items revealing within one batch
  const BATCH_GAP_MS = 220; // scroll-idle gap that resets the stagger counter
  const REDUCED =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const state = {
    observer: null,
    batchCount: 0,
    lastRevealAt: 0
  };

  function now() {
    return window.performance && performance.now ? performance.now() : Date.now();
  }

  function reveal(item, delayMs) {
    item.style.setProperty('--reveal-delay', delayMs + 'ms');
    item.setAttribute('data-revealed', 'true');
  }

  function revealAll(container) {
    container
      .querySelectorAll(':scope > :not([data-revealed="true"])')
      .forEach(function (item) {
        reveal(item, 0);
      });
  }

  function onIntersect(entries) {
    // Reveal in reading order so the stagger cascades top-left -> bottom-right;
    // IntersectionObserver does not guarantee entry order.
    const visible = entries.filter(function (entry) {
      return entry.isIntersecting;
    });
    visible.sort(function (a, b) {
      const ra = a.boundingClientRect;
      const rb = b.boundingClientRect;
      return ra.top - rb.top || ra.left - rb.left;
    });
    visible.forEach(function (entry) {
      const at = now();
      if (at - state.lastRevealAt > BATCH_GAP_MS) {
        state.batchCount = 0;
      }
      reveal(entry.target, state.batchCount * STEP_MS);
      state.batchCount += 1;
      state.lastRevealAt = at;
      state.observer.unobserve(entry.target);
    });
  }

  function ensureObserver() {
    if (!state.observer && 'IntersectionObserver' in window) {
      state.observer = new IntersectionObserver(onIntersect, {
        threshold: 0.15,
        rootMargin: '0px 0px -5% 0px'
      });
    }
    return state.observer;
  }

  function observe(container) {
    if (REDUCED || !ensureObserver()) {
      revealAll(container);
      return;
    }
    container.querySelectorAll(':scope > *').forEach(function (item) {
      state.observer.observe(item);
    });
  }

  // Public API so controllers that swap content in (e.g. AJAX pagination) can
  // re-arm the reveal on freshly-mounted [data-reveal] containers.
  window.Reveal = {
    observe: observe,
    revealAll: revealAll
  };

  function init() {
    document.querySelectorAll('[data-reveal]').forEach(observe);
  }

  document.addEventListener('DOMContentLoaded', init);
})();
